import React, { useState, useEffect, useCallback } from 'react';
import Countdown from './Countdown';
import ProgressBar from './ProgressBar';
import {
  formatCurrency, formatDate, getTripTotals, getPaymentStatus,
  getMotivationalMessage, getPerMonth, getDaysUntil, TRIP_TYPES,
} from '../utils/helpers';
import { fetchFlightStatus } from '../utils/storage';

const statusConfig = {
  paid: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: '✅ Paid' },
  overdue: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: '⚠️ Overdue' },
  'due-soon': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: '⏰ Due soon' },
  upcoming: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', badge: '📅 Upcoming' },
};

export default function HolidayDetail({ holiday, members, onBack, onTogglePaid, onAddPayment, onDeleteExpense, onEdit, onPromoteToDream, onDeleteHoliday }) {
  const { totalCost, totalPaid, outstanding, progress } = getTripTotals(holiday);
  const tripMembers = members.filter(m => holiday.familyMembers.includes(m.id));
  const isDream = holiday.status === 'dream';
  const dreamTarget = holiday.totalBudget || 0;
  const tripType = TRIP_TYPES.find(t => t.id === holiday.tripType);
  const hasFlights = !!(holiday.outboundFlight || holiday.returnFlight);

  // Flight status state
  const [flightData, setFlightData] = useState({});
  const [flightLoading, setFlightLoading] = useState({});
  const [flightErrors, setFlightErrors] = useState({});

  const loadFlight = useCallback(async (flightNumber, date) => {
    if (!flightNumber) return;
    setFlightLoading(prev => ({ ...prev, [flightNumber]: true }));
    setFlightErrors(prev => ({ ...prev, [flightNumber]: null }));
    try {
      const data = await fetchFlightStatus(flightNumber, date);
      setFlightData(prev => ({ ...prev, [flightNumber]: data }));
    } catch (err) {
      setFlightErrors(prev => ({ ...prev, [flightNumber]: err.message }));
    } finally {
      setFlightLoading(prev => ({ ...prev, [flightNumber]: false }));
    }
  }, []);

  useEffect(() => {
    if (!hasFlights) return;
    // Only auto-fetch if departure is within 2 days
    const daysUntil = getDaysUntil(holiday.departureDate);
    if (daysUntil > 2) return;
    if (holiday.outboundFlight) loadFlight(holiday.outboundFlight, holiday.departureDate);
    if (holiday.returnFlight) loadFlight(holiday.returnFlight, holiday.returnDate || holiday.departureDate);
  }, [hasFlights, holiday.outboundFlight, holiday.returnFlight, holiday.departureDate, holiday.returnDate, loadFlight]);

  // Family cost splitting — only between members who split costs
  const costSplitters = tripMembers.filter(m => m.splitCosts !== false);
  const splitCount = costSplitters.length || 1;
  const perPersonShare = totalCost / splitCount;

  // Track how much each cost-splitting member has contributed
  const memberPaid = {};
  costSplitters.forEach(m => { memberPaid[m.id] = 0; });

  // Shared payments get split evenly across cost splitters, personal ones count to that person
  holiday.expenses.forEach(exp => {
    if (!exp.isPaid) return;
    if (exp.assignedTo === 'shared') {
      const each = exp.amount / splitCount;
      costSplitters.forEach(m => { memberPaid[m.id] += each; });
    } else if (memberPaid[exp.assignedTo] !== undefined) {
      memberPaid[exp.assignedTo] += exp.amount;
    }
  });

  // Sort expenses by due date
  const sortedExpenses = [...holiday.expenses].sort((a, b) => {
    if (a.isPaid !== b.isPaid) return a.isPaid ? 1 : -1;
    return new Date(a.dueDate) - new Date(b.dueDate);
  });

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-sm text-purple-600 hover:text-purple-800 font-medium mb-4 flex items-center gap-1"
        >
          ← Back to Holidays
        </button>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <span className="text-5xl">{holiday.emoji}</span>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">{holiday.destination}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-gray-500">
                    {formatDate(holiday.departureDate)}
                    {holiday.returnDate ? ` — ${formatDate(holiday.returnDate)}` : ''}
                  </p>
                  {tripType && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      {tripType.icon} {tripType.label}
                    </span>
                  )}
                </div>
                {tripMembers.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {tripMembers.map(m => (
                      <span key={m.id} className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: m.colour }}>
                        {m.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="text-center">
              <Countdown date={holiday.departureDate} large />
            </div>
          </div>

          {/* Financial summary */}
          {/* Financial summary */}
          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-purple-50 rounded-xl p-3 sm:p-4 text-center min-w-0">
              <div className="text-xs sm:text-sm text-purple-600 font-medium">{isDream ? 'Target' : 'Total Cost'}</div>
              <div className="text-lg sm:text-2xl font-bold text-purple-700 truncate">{formatCurrency(isDream ? dreamTarget : totalCost)}</div>
            </div>
            <div className="bg-teal-50 rounded-xl p-3 sm:p-4 text-center min-w-0">
              <div className="text-xs sm:text-sm text-teal-600 font-medium">{isDream ? 'Saved' : 'Paid'}</div>
              <div className="text-lg sm:text-2xl font-bold text-teal-700 truncate">{formatCurrency(totalPaid)}</div>
            </div>
            <div className="bg-coral-50 rounded-xl p-3 sm:p-4 text-center min-w-0">
              <div className="text-xs sm:text-sm text-coral-600 font-medium">{isDream ? 'Still Needed' : 'Outstanding'}</div>
              <div className="text-lg sm:text-2xl font-bold text-coral-600 truncate">
                {formatCurrency(isDream ? Math.max(0, dreamTarget - totalPaid) : outstanding)}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <ProgressBar progress={isDream ? (dreamTarget > 0 ? (totalPaid / dreamTarget) * 100 : 0) : progress} height="h-4" />
          </div>

          {/* Payment deadline & per-month breakdown */}
          {holiday.paymentDeadline && outstanding > 0 && (() => {
            const pm = getPerMonth(outstanding, holiday.paymentDeadline);
            const deadlineDays = getDaysUntil(holiday.paymentDeadline);
            const isUrgent = deadlineDays <= 30 && deadlineDays > 0;
            const isOverdue = deadlineDays < 0;
            return (
              <div className={`mt-4 rounded-xl p-4 border-2 ${
                isOverdue ? 'bg-red-50 border-red-200' : isUrgent ? 'bg-amber-50 border-amber-200' : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className={`text-sm font-semibold ${isOverdue ? 'text-red-700' : isUrgent ? 'text-amber-700' : 'text-purple-700'}`}>
                      {isOverdue ? '⚠️ Payment deadline passed!' : `📅 Payment deadline: ${formatDate(holiday.paymentDeadline)}`}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {isOverdue
                        ? `Was ${Math.abs(deadlineDays)} day${Math.abs(deadlineDays) !== 1 ? 's' : ''} ago`
                        : `${deadlineDays} day${deadlineDays !== 1 ? 's' : ''} away`
                      }
                    </div>
                  </div>
                  {pm && pm.months > 0 && (
                    <div className="text-right">
                      <div className="text-2xl font-bold text-purple-700">
                        💷 {formatCurrency(pm.amount)}
                      </div>
                      <div className="text-xs font-medium text-purple-500">per month ({pm.months} payday{pm.months !== 1 ? 's' : ''} left)</div>
                    </div>
                  )}
                  {pm && pm.months === 0 && (
                    <div className="text-right">
                      <div className="text-xl font-bold text-red-600">
                        {formatCurrency(outstanding)} due now
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          <p className="text-center text-sm text-gray-500 italic mt-3">{getMotivationalMessage(holiday)}</p>

          {/* Action buttons */}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button
              onClick={() => onEdit(holiday)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-semibold text-gray-600 transition-colors"
            >
              ✏️ Edit Trip
            </button>
            <button
              onClick={() => onAddPayment(holiday.id)}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 rounded-xl text-sm font-semibold text-white transition-colors"
            >
              + Add Payment
            </button>
            {isDream && (
              <button
                onClick={() => onPromoteToDream(holiday.id)}
                className="px-4 py-2 bg-green-100 hover:bg-green-200 rounded-xl text-sm font-semibold text-green-700 transition-colors"
              >
                ✅ Promote to Booked
              </button>
            )}
            <button
              onClick={() => {
                if (window.confirm(`Delete "${holiday.destination}"? This cannot be undone.`)) {
                  onDeleteHoliday(holiday.id);
                }
              }}
              className="px-4 py-2 bg-red-50 hover:bg-red-100 rounded-xl text-sm font-semibold text-red-500 transition-colors"
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      </div>

      {/* Notes */}
      {holiday.notes && (
        <div className="bg-sunny-50 border border-sunny-200 rounded-2xl p-4 mb-6">
          <div className="text-sm font-semibold text-sunny-700 mb-1">📝 Notes</div>
          <p className="text-gray-700">{holiday.notes}</p>
        </div>
      )}

      {/* Flight status */}
      {hasFlights && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-3">Flight Status</h2>
          <div className="space-y-3">
            {[
              { label: 'Outbound', number: holiday.outboundFlight, date: holiday.departureDate },
              { label: 'Return', number: holiday.returnFlight, date: holiday.returnDate || holiday.departureDate },
            ].filter(f => f.number).map(({ label, number, date: flightDate }) => {
              const data = flightData[number];
              const loading = flightLoading[number];
              const error = flightErrors[number];

              return (
                <div key={number} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-xs font-medium text-gray-400 uppercase">{label}</span>
                        <div className="font-bold text-gray-800 text-lg">{number}</div>
                        {data?.airline && <div className="text-xs text-gray-500">{data.airline}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        {data?.status && (
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                            data.status === 'landed' ? 'bg-green-100 text-green-700' :
                            data.status === 'active' ? 'bg-blue-100 text-blue-700' :
                            data.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            data.status === 'diverted' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
                          </span>
                        )}
                        <button
                          onClick={() => loadFlight(number, flightDate)}
                          disabled={loading}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                          title="Refresh flight status"
                        >
                          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {loading && !data && (
                      <div className="text-sm text-gray-400 text-center py-3">Loading flight data...</div>
                    )}

                    {error && !data && (
                      <div className="text-sm text-gray-400 text-center py-3">{error}</div>
                    )}

                    {!data && !loading && !error && (
                      <div className="text-center py-3">
                        <button
                          onClick={() => loadFlight(number, flightDate)}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          Check flight status
                        </button>
                      </div>
                    )}

                    {data && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-xl p-3">
                          <div className="text-xs font-medium text-gray-400 mb-1">Departure</div>
                          <div className="font-semibold text-gray-700 text-sm">{data.departure.airport || '—'}</div>
                          <div className="text-xs text-gray-500">{data.departure.iata || ''}</div>
                          {data.departure.terminal && (
                            <div className="text-xs text-gray-500 mt-1">Terminal {data.departure.terminal}</div>
                          )}
                          {data.departure.gate && (
                            <div className="text-sm font-bold text-purple-600 mt-1">Gate {data.departure.gate}</div>
                          )}
                          {data.departure.scheduled && (
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(data.departure.scheduled).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {data.departure.delay > 0 && (
                                <span className="text-red-500 font-medium ml-1">(+{data.departure.delay}min)</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                          <div className="text-xs font-medium text-gray-400 mb-1">Arrival</div>
                          <div className="font-semibold text-gray-700 text-sm">{data.arrival.airport || '—'}</div>
                          <div className="text-xs text-gray-500">{data.arrival.iata || ''}</div>
                          {data.arrival.terminal && (
                            <div className="text-xs text-gray-500 mt-1">Terminal {data.arrival.terminal}</div>
                          )}
                          {data.arrival.gate && (
                            <div className="text-sm font-bold text-purple-600 mt-1">Gate {data.arrival.gate}</div>
                          )}
                          {data.arrival.scheduled && (
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(data.arrival.scheduled).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {data.arrival.delay > 0 && (
                                <span className="text-red-500 font-medium ml-1">(+{data.arrival.delay}min)</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payment schedule */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-3">Payment Schedule</h2>
        {sortedExpenses.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-400 border border-gray-100">
            No payments yet. Add your first payment above!
          </div>
        ) : (
          <div className="space-y-2">
            {sortedExpenses.map(expense => {
              const st = getPaymentStatus(expense);
              const cfg = statusConfig[st];
              const assignee = expense.assignedTo === 'shared'
                ? null
                : members.find(m => m.id === expense.assignedTo);

              return (
                <div
                  key={expense.id}
                  className={`${cfg.bg} ${cfg.border} border rounded-xl p-4 flex items-center gap-4 transition-all`}
                >
                  <button
                    onClick={() => onTogglePaid(holiday.id, expense.id)}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm paid-check transition-all ${
                      expense.isPaid
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-300 hover:border-purple-400 text-transparent hover:text-purple-300'
                    }`}
                  >
                    ✓
                  </button>

                  <div className="flex-1 min-w-0">
                    <span className={`font-semibold ${expense.isPaid ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                      {expense.description}
                    </span>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      {expense.dueDate && <span>{formatDate(expense.dueDate)}</span>}
                      {assignee && (
                        <span className="px-1.5 py-0.5 rounded-full text-white text-xs" style={{ backgroundColor: assignee.colour }}>
                          {assignee.name}
                        </span>
                      )}
                      {expense.assignedTo === 'shared' && (
                        <span className="px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 text-xs">Shared</span>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className={`font-bold ${expense.isPaid ? 'text-gray-400' : 'text-gray-800'}`}>
                      {formatCurrency(expense.amount)}
                    </div>
                    <span className={`text-xs font-medium ${cfg.text}`}>{cfg.badge}</span>
                  </div>

                  <button
                    onClick={() => {
                      if (window.confirm('Delete this expense?')) {
                        onDeleteExpense(holiday.id, expense.id);
                      }
                    }}
                    className="text-gray-300 hover:text-red-400 transition-colors text-sm"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Family cost splitting */}
      {costSplitters.length > 1 && totalCost > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-3">Family Cost Splitting</h2>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 bg-purple-50 border-b border-purple-100">
              <div className="text-sm text-purple-600 font-medium">Who Owes What</div>
              <div className="text-xs text-purple-500 mt-0.5">
                {formatCurrency(totalCost)} split {splitCount} ways = <strong>{formatCurrency(perPersonShare)}</strong> each
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {costSplitters.map(m => {
                const paid = memberPaid[m.id] || 0;
                const owes = Math.max(0, perPersonShare - paid);
                const overpaid = Math.max(0, paid - perPersonShare);
                const paidPercent = perPersonShare > 0 ? Math.min(100, (paid / perPersonShare) * 100) : 0;

                return (
                  <div key={m.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: m.colour }}>
                          {m.name[0]}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">{m.name}</span>
                          <div className="text-xs text-gray-400">
                            Paid {formatCurrency(paid)} of {formatCurrency(perPersonShare)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {owes > 0 ? (
                          <div>
                            <div className="font-bold text-coral-500 text-lg">
                              owes {formatCurrency(owes)}
                            </div>
                            {holiday.paymentDeadline && (() => {
                              const pm = getPerMonth(owes, holiday.paymentDeadline);
                              if (!pm) return null;
                              return (
                                <div className="text-xs text-gray-400">
                                  {pm.months > 0
                                    ? `${formatCurrency(pm.amount)}/mo for ${pm.months} payday${pm.months !== 1 ? 's' : ''}`
                                    : 'due now'
                                  }
                                </div>
                              );
                            })()}
                          </div>
                        ) : overpaid > 0 ? (
                          <div>
                            <div className="font-bold text-green-500">All paid ✓</div>
                            <div className="text-xs text-teal-500">{formatCurrency(overpaid)} overpaid</div>
                          </div>
                        ) : (
                          <div className="font-bold text-green-500">All paid ✓</div>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${paidPercent >= 100 ? 'bg-green-400' : 'bg-purple-400'}`}
                        style={{ width: `${paidPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
